import React from 'react';
import { PublicClientApplication, Configuration, PopupRequest } from '@azure/msal-browser';

const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID || '';
const MICROSOFT_REDIRECT_URI =
  process.env.REACT_APP_MICROSOFT_REDIRECT_URI ||
  `${window.location.origin.replace(/\/+$/, '')}/`;

// MSAL configuration
const msalConfig: Configuration = {
  auth: {
    clientId: MICROSOFT_CLIENT_ID,
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: MICROSOFT_REDIRECT_URI,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

// Login request
const loginRequest: PopupRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
};

// Create MSAL instance
let msalInstance: PublicClientApplication | null = null;

if (MICROSOFT_CLIENT_ID) {
  msalInstance = new PublicClientApplication(msalConfig);
}

interface MicrosoftLoginButtonProps {
  onSuccess: (accessToken: string) => void;
  onError?: (error: Error) => void;
  className?: string;
  disabled?: boolean;
}

// Microsoft icon SVG
const MicrosoftIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
    <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
  </svg>
);

const MicrosoftLoginButton: React.FC<MicrosoftLoginButtonProps> = ({
  onSuccess,
  onError,
  className = '',
  disabled = false,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const handleLogin = async () => {
    if (!msalInstance) {
      onError?.(new Error('Microsoft login is not configured'));
      return;
    }

    setIsLoading(true);

    try {
      // Initialize MSAL
      await msalInstance.initialize();
      
      // Login with popup
      const response = await msalInstance.loginPopup(loginRequest);
      
      if (response.accessToken) {
        onSuccess(response.accessToken);
      } else {
        throw new Error('No access token received');
      }
    } catch (error) {
      console.error('Microsoft login error:', error);
      onError?.(error instanceof Error ? error : new Error('Microsoft login failed'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!MICROSOFT_CLIENT_ID) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleLogin}
      disabled={disabled || isLoading}
      className={`flex items-center justify-center gap-2 w-full bg-white hover:bg-gray-100 text-gray-700 font-medium py-2.5 px-4 rounded border border-gray-300 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      style={{ minHeight: '40px' }}
    >
      <MicrosoftIcon />
      {isLoading ? 'Signing in...' : 'Continue with Microsoft'}
    </button>
  );
};

export default MicrosoftLoginButton;

